import { type AppNode } from '../../types';

export type CropTargetPartition = {
  /** Groups that crop as one frame. */
  groupIds: string[];
  /** Images that keep their own frame. */
  looseNodeIds: string[];
};

/**
 * One frame per group, plus one frame per image that is not part of those groups.
 * A group qualifies when at least two selected images belong to it, or when every
 * selected image is grouped. A single grouped image next to loose images stays its own frame.
 */
export const partitionCropTargets = (nodes: AppNode[], cropNodeIds: string[]): CropTargetPartition | null => {
  if (cropNodeIds.length < 2) {
    return null;
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const groupIds: string[] = [];
  const counts = new Map<string, number>();
  let hasUngrouped = false;

  for (const id of cropNodeIds) {
    const groupId = nodeById.get(id)?.groupId;

    if (!groupId) {
      hasUngrouped = true;
      continue;
    }

    counts.set(groupId, (counts.get(groupId) ?? 0) + 1);

    if (!groupIds.includes(groupId)) {
      groupIds.push(groupId);
    }
  }

  if (!hasUngrouped) {
    return groupIds.length > 0 ? { groupIds, looseNodeIds: [] } : null;
  }

  const compositionIds = groupIds.filter((groupId) => (counts.get(groupId) ?? 0) >= 2);

  if (compositionIds.length === 0) {
    return null;
  }

  const looseNodeIds = cropNodeIds.filter((id) => {
    const groupId = nodeById.get(id)?.groupId;

    return !groupId || !compositionIds.includes(groupId);
  });

  return { groupIds: compositionIds, looseNodeIds };
};

/**
 * Every crop target belongs to a group. One frame covers each distinct group.
 * An ungrouped image in the set is not this case; see `partitionCropTargets`.
 */
export const resolveCropGroupIds = (nodes: AppNode[], cropNodeIds: string[]): string[] | null => {
  const partition = partitionCropTargets(nodes, cropNodeIds);

  if (!partition || partition.looseNodeIds.length > 0) {
    return null;
  }

  return partition.groupIds;
};

/** One shared group is a single composition. Several groups are one frame each. */
export const resolveCropGroupId = (nodes: AppNode[], cropNodeIds: string[]): string | null => {
  const groupIds = resolveCropGroupIds(nodes, cropNodeIds);

  return groupIds?.length === 1 ? groupIds[0] : null;
};
